import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    AlertIcon,
    Badge,
    Box,
    Flex,
    HStack,
    Heading,
    Icon,
    IconButton,
    Spinner,
    Text,
    Tr,
    VStack,
    useToast,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { useGetIntegrations } from '../../../hooks/useGetIntegrations';
import { useUpsertIntegration } from '../../../hooks/useSaveIntegration';
import SessionContext, { isInvoicingOrgSession } from '../../../contexts/SessionContext';
import { canAccessIntegrations } from '../../../utils/integrationsAccess';
import CopyButton from '../../CopyButton';
import PartBayButton from '../../PartBayButton';
import { PtAdditiveFlow } from '../PtAdditiveFlow';
import {
    SettingsEmptyState,
    SettingsSubTable,
    SettingsTd,
    SettingsTh,
    useSettingsDetailCrumb,
    useSubTablePagination,
} from '../primitives';
import { PARTBAY_BLUE, SETTINGS_CONTENT_MAX_WIDTH } from '../primitives/constants';
import ConnectionBadge from './ConnectionBadge';
import AddCredentialModal, { type AddCredentialPayload } from './AddCredentialModal';
import NexGuidModal from './NexGuidModal';
import {
    CONNECTIONS,
    describeCoverage,
    getConnectionStatus,
    getCredentialRows,
    isConnectionId,
    isListedConnection,
    type ConnectionId,
} from './catalog';

/** Value plus a copy icon, for anything a user would otherwise retype by hand. */
const CopyableValue = ({
    value,
    label,
    isMono = false,
}: {
    value: string | null;
    label: string;
    isMono?: boolean;
}) => {
    if (!value) {
        return (
            <Text fontSize="sm" color="gray.400" fontStyle="italic">
                Not set
            </Text>
        );
    }
    return (
        <HStack spacing={1} minW={0}>
            <Text
                fontSize="sm"
                color="gray.700"
                fontFamily={isMono ? 'mono' : undefined}
                wordBreak="break-all"
            >
                {value}
            </Text>
            <CopyButton text={value} label={`Copy ${label}`} />
        </HStack>
    );
};

/** Masked password with a reveal toggle; copying works without revealing. */
const PasswordValue = ({
    value,
    isRevealed,
    onToggleReveal,
}: {
    value: string | null;
    isRevealed: boolean;
    onToggleReveal: () => void;
}) => {
    if (!value) {
        return (
            <Text fontSize="sm" color="gray.400" fontStyle="italic">
                Not set
            </Text>
        );
    }
    return (
        <HStack spacing={1} minW={0}>
            <Text fontSize="sm" fontFamily="mono" color="gray.700" wordBreak="break-all">
                {isRevealed ? value : '•'.repeat(Math.min(value.length, 12))}
            </Text>
            <IconButton
                aria-label={isRevealed ? 'Hide password' : 'Reveal password'}
                size="xs"
                variant="ghost"
                color="gray.400"
                _hover={{ color: 'gray.600' }}
                icon={<Icon as={(isRevealed ? FaEyeSlash : FaEye) as React.ElementType} w={3} h={3} />}
                onClick={onToggleReveal}
            />
            <CopyButton text={value} label="Copy password" />
        </HStack>
    );
};

export interface ConnectionDetailSectionProps {
    /** Raw :connectionId route param — validated here, not by the router. */
    connectionId: string;
}

/**
 * A single connection's page, reached from the Connections list. Shows every
 * stored credential in one table and lets a new one be added; existing rows are
 * read-only by design, so a working login can never be edited or deleted from
 * here. Values are copyable so they can be pasted straight into the provider.
 */
const ConnectionDetailSection = ({ connectionId }: ConnectionDetailSectionProps) => {
    const { data, isLoading, isError, error } = useGetIntegrations();
    const sessionContext = useContext(SessionContext);
    const navigate = useNavigate();
    const toast = useToast();

    const [revealedRows, setRevealedRows] = useState<Record<string, boolean>>({});
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [isNexModalOpen, setIsNexModalOpen] = useState(false);
    const [isPtFlowOpen, setIsPtFlowOpen] = useState(false);

    const id: ConnectionId | null =
        isConnectionId(connectionId) &&
        isListedConnection(connectionId, { invoicingOnly: isInvoicingOrgSession(sessionContext) })
            ? connectionId
            : null;
    const definition = id ? CONNECTIONS[id] : null;

    useSettingsDetailCrumb(definition?.label);

    // An unknown provider in the URL is a dead end — send the user back to the list.
    useEffect(() => {
        if (!id) navigate('/settings/connections', { replace: true });
    }, [id, navigate]);

    const upsertMutation = useUpsertIntegration({
        onError: (err) => {
            toast({
                title: 'Failed to save credential',
                description: err.message,
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        },
    });

    const rows = useMemo(() => (id && data ? getCredentialRows(id, data) : []), [id, data]);
    const usedLocationIds = useMemo(
        () => new Set(rows.map((row) => row.locationId).filter(Boolean) as string[]),
        [rows],
    );
    const { currentPage, setCurrentPage, totalPages, pageItems } = useSubTablePagination(rows);

    if (!canAccessIntegrations(sessionContext)) {
        return (
            <Box px={8} pt={6}>
                <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    Integrations are managed by Yaxle. Contact support@yaxle.com to make changes.
                </Alert>
            </Box>
        );
    }

    if (isLoading) {
        return (
            <Flex justify="center" py={12}>
                <Spinner size="lg" color={PARTBAY_BLUE} />
            </Flex>
        );
    }

    if (isError) {
        return (
            <Box px={8} pt={6}>
                <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    {error?.message || 'Failed to load connections.'}
                </Alert>
            </Box>
        );
    }

    if (!data || !id || !definition) return null;

    // PartsTrader's additive flow owns the whole screen while it runs.
    if (isPtFlowOpen) {
        return (
            <PtAdditiveFlow
                onComplete={() => {
                    setIsPtFlowOpen(false);
                    toast({
                        title: 'PartsTrader setup complete',
                        status: 'success',
                        duration: 4000,
                        isClosable: true,
                    });
                }}
                onCancel={() => setIsPtFlowOpen(false)}
                mitchellCredentialLocationIds={
                    new Set(
                        getCredentialRows('mitchell', data)
                            .map((row) => row.locationId)
                            .filter(Boolean) as string[],
                    )
                }
                opsCredentialLocationIds={
                    new Set(
                        getCredentialRows('opstrax', data)
                            .map((row) => row.locationId)
                            .filter(Boolean) as string[],
                    )
                }
            />
        );
    }

    const isNex = id === 'nex';
    const nexCustomerGuid = data.nex?.customerGuid ?? null;
    const status = getConnectionStatus(id, data);
    // Coverage lives in the table's footer rather than the page subheader, so it
    // reads as a fact about the rows above it.
    const coverage = describeCoverage(status);
    const coverageLabel = coverage ? (
        <Text fontSize="xs" color="gray.500">
            {coverage}
        </Text>
    ) : undefined;
    const availableLocations = data.locations.filter(
        (location) => !usedLocationIds.has(location._id),
    );
    // Per-location connections run out of things to add once every location has
    // a credential; Nexsyis holds exactly one GUID.
    const canAdd = isNex
        ? !nexCustomerGuid
        : definition.isAdditive && (!definition.isPerLocation || availableLocations.length > 0);
    const addLabel = (() => {
        if (canAdd) return isNex ? 'Add Connection' : 'Add Credential';
        if (isNex) return 'Connected';
        if (definition.isPerLocation) return 'All locations covered';
        return 'Add Credential';
    })();

    const handleSaveNew = async (payload: AddCredentialPayload) => {
        // Nexsyis has no username/password pair; its GUID goes through NexGuidModal.
        if (id === 'nex') return;
        await upsertMutation.mutateAsync({
            provider: id,
            credentials: [
                {
                    username: payload.username,
                    password: payload.password,
                    ...(definition.isPerLocation && payload.locationId
                        ? { locationId: payload.locationId }
                        : {}),
                },
            ],
        });
        toast({
            title: 'Credential added',
            status: 'success',
            duration: 3000,
            isClosable: true,
        });
    };

    const hasRows = isNex ? !!nexCustomerGuid : rows.length > 0;

    return (
        <Flex direction="column" h="100%" minH={0} w="100%">
            <Box flex={1} minH={0} overflowY="auto" px={8} pt={6} pb={8}>
                <Box maxW={SETTINGS_CONTENT_MAX_WIDTH} mx="auto">
                    <Flex justify="space-between" align="flex-start" gap={4} pb={6}>
                        <HStack spacing={3} align="center" minW={0}>
                            <ConnectionBadge id={id} size={40} />
                            <Box minW={0}>
                                <HStack spacing={2}>
                                    <Heading size="md" color="gray.800">
                                        {definition.label}
                                    </Heading>
                                    <Badge
                                        colorScheme={status.isConnected ? 'green' : 'gray'}
                                        variant="subtle"
                                    >
                                        {status.isConnected ? 'Connected' : 'Not connected'}
                                    </Badge>
                                </HStack>
                                <Text fontSize="sm" color="gray.500" mt={1}>
                                    {definition.description}
                                </Text>
                            </Box>
                        </HStack>
                        {(definition.isAdditive || isNex) && hasRows && (
                            <PartBayButton
                                text={addLabel}
                                variant="solid"
                                colorScheme="partbay-blue"
                                size="sm"
                                onClick={() => (isNex ? setIsNexModalOpen(true) : setIsAddOpen(true))}
                                isDisabled={!canAdd}
                            />
                        )}
                    </Flex>

                    <VStack spacing={4} align="stretch">
                        {!hasRows ? (
                            <SettingsEmptyState
                                variant="inset"
                                icon="plugConnected"
                                title={
                                    isNex
                                        ? 'No Nexsyis connection configured yet'
                                        : `No ${definition.label} credentials configured yet`
                                }
                                description={
                                    canAdd
                                        ? `Add your first ${isNex ? 'connection' : 'credential'} to get started`
                                        : undefined
                                }
                                action={
                                    canAdd
                                        ? {
                                              label: addLabel,
                                              onClick: () =>
                                                  isNex
                                                      ? setIsNexModalOpen(true)
                                                      : setIsAddOpen(true),
                                          }
                                        : undefined
                                }
                            />
                        ) : isNex ? (
                            <SettingsSubTable
                                head={<SettingsTh>Customer GUID</SettingsTh>}
                                footerLeft={coverageLabel}
                            >
                                <Tr>
                                    <SettingsTd>
                                        <CopyableValue
                                            value={nexCustomerGuid}
                                            label="customer GUID"
                                            isMono
                                        />
                                    </SettingsTd>
                                </Tr>
                            </SettingsSubTable>
                        ) : (
                            <SettingsSubTable
                                head={
                                    <>
                                        {definition.isPerLocation && <SettingsTh>Location</SettingsTh>}
                                        <SettingsTh>Username</SettingsTh>
                                        <SettingsTh>Password</SettingsTh>
                                    </>
                                }
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                                footerLeft={coverageLabel}
                            >
                                {pageItems.map((row) => (
                                    <Tr key={row._id}>
                                        {definition.isPerLocation && (
                                            <SettingsTd fontWeight="medium" color="gray.800">
                                                {row.locationTitle ?? '—'}
                                            </SettingsTd>
                                        )}
                                        <SettingsTd>
                                            <CopyableValue
                                                value={row.username || null}
                                                label="username"
                                            />
                                        </SettingsTd>
                                        <SettingsTd>
                                            <PasswordValue
                                                value={row.password}
                                                isRevealed={!!revealedRows[row._id]}
                                                onToggleReveal={() =>
                                                    setRevealedRows((previous) => ({
                                                        ...previous,
                                                        [row._id]: !previous[row._id],
                                                    }))
                                                }
                                            />
                                        </SettingsTd>
                                    </Tr>
                                ))}
                            </SettingsSubTable>
                        )}
                    </VStack>
                </Box>
            </Box>

            <AddCredentialModal
                connectionId={isAddOpen ? id : null}
                onClose={() => setIsAddOpen(false)}
                locations={data.locations}
                usedLocationIds={definition.isPerLocation ? usedLocationIds : new Set()}
                onSave={handleSaveNew}
                isSaving={upsertMutation.isPending}
            />

            <NexGuidModal
                isOpen={isNexModalOpen}
                onClose={() => setIsNexModalOpen(false)}
                currentGuid={nexCustomerGuid}
            />
        </Flex>
    );
};

export default ConnectionDetailSection;
